import { useState, useEffect, useCallback } from 'react';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { Button } from '@/components/ui/button';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import VideoModal from './VideoModal';

const HERO_IMAGES = [
  '/uploads/hero-home-watercolor.jpg',
  '/uploads/hero-home-rotate-white.jpg',
  '/uploads/hero-home-rotate-asian.jpg',
  '/uploads/hero-home-rotate-latino.jpg',
  '/uploads/hero-home-rotate-solo-man.jpg',
];

const ROTATION_INTERVAL = 10000; // 10 seconds, gentle pace for seniors

const HomeHeroOS = () => {
  const { openModal } = useEarlyAdopter();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = setInterval(advance, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [advance, prefersReducedMotion]);

  const handlePrimaryCTA = () => {
    openModal('Homepage Hero - Get Early Access');
  };
  const handleSecondaryCTA = () => {
    setVideoModalOpen(true);
  };

  return (
    <section className="relative w-full min-h-[85vh] lg:min-h-[90vh] overflow-hidden">
      {/* Rotating background images, stacked, cross-fade via opacity */}
      {HERO_IMAGES.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ease-in-out"
          style={{
            backgroundImage: `url(${src})`,
            opacity: i === activeIndex ? 1 : 0,
          }}
          aria-hidden="true"
        />
      ))}

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />

      {/* Content, stays completely stable during rotation */}
      <div className="relative h-full min-h-[85vh] lg:min-h-[90vh] flex items-center">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl pt-24 md:pt-32 pb-20 md:pb-28">
            <div className="bg-black/50 backdrop-blur-sm rounded-md p-8 md:p-10 text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 tracking-tight leading-[1.1] drop-shadow-lg">
                Thrive at Home with Confidence.
              </h1>

              <p className="text-xl sm:text-2xl lg:text-3xl text-white/90 font-bold mb-6 drop-shadow-md">
                The quiet support system that keeps you independent in your own home, without changing how you live.
              </p>

              <p className="text-base sm:text-lg md:text-xl text-white/85 mb-8 leading-relaxed max-w-xl drop-shadow-raised">
                SeniorThrive is a private layer of support that strengthens your daily rhythm
                and simplifies home safety. By turning everyday observations into helpful insights,
                we provide the tools you need to stay in the driver's seat of your own life,
                ensuring your home remains the place where you feel most capable, connected,
                and in control.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <Button onClick={handlePrimaryCTA} size="lg" className="text-lg px-8 py-6 min-h-[56px] rounded-sm font-bold bg-primary hover:bg-black/50 text-primary-foreground shadow-overlay">
                  Get Early Access
                </Button>

                <Button onClick={handleSecondaryCTA} variant="outline" size="lg" className="text-lg px-8 py-6 min-h-[48px] rounded-sm font-bold gap-2 bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 hover:text-white">
                  <PlayCircleIcon className="w-5 h-5" />
                  See How It Works
                </Button>
              </div>

              <p className="text-base text-white/85 mb-2">
                SeniorThrive is currently in early access. New members are invited in stages.
              </p>
              <p className="text-sm text-white/70">
                Private. Secure. Always on your terms.
              </p>
            </div>
          </div>
        </div>
      </div>

      <VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} />
    </section>
  );
};

export default HomeHeroOS;
